import React from 'react';
import FooterSection from './Homepage/components/FooterSection';

const Privacy = () => {
  return (
    <>
      <main className="min-h-screen bg-slate-50 text-slate-900 flex flex-col">
        <div className="max-w-3xl mx-auto px-4 py-10 md:py-14 flex-1">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
            Privacy Policy
          </h1>
          <p className="text-sm md:text-base text-slate-600 mb-2">
            <strong>Last updated:</strong> December 7, 2025
          </p>

          <section className="space-y-4 mt-6">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              1. Introduction
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              CleanBid.ai (&quot;Company,&quot; &quot;we,&quot; &quot;our,&quot; or
              &quot;us&quot;) provides software tools for service businesses to
              manage leads, send communications, automate workflows, and operate
              efficiently. This Privacy Policy explains how we collect, use,
              disclose, and safeguard your information when you visit our website
              (cleanbid.ai), use the CleanBid SaaS platform, interact with our
              application, or communicate with us.
            </p>
            <p className="text-sm md:text-base text-slate-700">
              By accessing or using CleanBid.ai, you agree to the practices
              described in this Privacy Policy.
            </p>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              2. Information We Collect
            </h2>
            <h3 className="text-sm md:text-base font-semibold text-slate-900">
              2.1 Information You Provide to Us
            </h3>
            <p className="text-sm md:text-base text-slate-700">
              We may collect personal information that you provide directly to us,
              including:
            </p>
            <ul className="list-disc pl-5 text-sm md:text-base text-slate-700 space-y-1">
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Business information</li>
              <li>Login credentials</li>
              <li>
                Payment information (processed securely by third-party payment
                processors such as Stripe; CleanBid.ai does not store full payment
                card numbers)
              </li>
              <li>
                Messages sent or received through our platform, including SMS and
                other communications
              </li>
              <li>Any data you upload or enter into your CleanBid account</li>
            </ul>

            <h3 className="text-sm md:text-base font-semibold text-slate-900">
              2.2 Automatically Collected Information
            </h3>
            <p className="text-sm md:text-base text-slate-700">
              When you access the platform, we may automatically collect certain
              information, including:
            </p>
            <ul className="list-disc pl-5 text-sm md:text-base text-slate-700 space-y-1">
              <li>IP address</li>
              <li>Device type and operating system</li>
              <li>Browser type and version</li>
              <li>Usage data and page interaction information</li>
              <li>Referring/exit pages</li>
              <li>Cookies and similar tracking technologies</li>
              <li>Log files and diagnostic information</li>
            </ul>

            <h3 className="text-sm md:text-base font-semibold text-slate-900">
              2.3 SMS and Communication Data
            </h3>
            <p className="text-sm md:text-base text-slate-700">
              When using CleanBid&apos;s messaging features, we may collect:
            </p>
            <ul className="list-disc pl-5 text-sm md:text-base text-slate-700 space-y-1">
              <li>Content of inbound and outbound messages</li>
              <li>Recipient and sender phone numbers</li>
              <li>Timestamps and delivery status information</li>
              <li>
                Carrier metadata required for message routing and compliance
              </li>
            </ul>
            <p className="text-sm md:text-base text-slate-700">
              We do not sell SMS content or phone number data for third-party
              marketing purposes.
            </p>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              3. How We Use Your Information
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              We use the information we collect for purposes including:
            </p>
            <ul className="list-disc pl-5 text-sm md:text-base text-slate-700 space-y-1">
              <li>Providing, maintaining, and improving the CleanBid platform</li>
              <li>Delivering messages via carriers (e.g., Telnyx)</li>
              <li>
                Managing your account, including authentication and preferences
              </li>
              <li>Processing transactions and subscription payments</li>
              <li>Providing customer support and responding to inquiries</li>
              <li>Monitoring usage and preventing fraud or abuse</li>
              <li>
                Complying with legal obligations and enforcing our Terms of
                Service
              </li>
              <li>
                Personalizing the platform experience and improving product
                features
              </li>
            </ul>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              4. Legal Basis for Processing (EEA/UK)
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              If you are located in the European Economic Area (EEA) or the United
              Kingdom, we process your personal data under the following legal
              bases:
            </p>
            <ul className="list-disc pl-5 text-sm md:text-base text-slate-700 space-y-1">
              <li>Performance of a contract with you</li>
              <li>Compliance with legal obligations</li>
              <li>Legitimate business interests</li>
              <li>Your consent, where applicable</li>
            </ul>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              5. Sharing Your Information
            </h2>
            <h3 className="text-sm md:text-base font-semibold text-slate-900">
              5.1 Service Providers
            </h3>
            <p className="text-sm md:text-base text-slate-700">
              We may share your information with trusted third-party service
              providers who assist us with:
            </p>
            <ul className="list-disc pl-5 text-sm md:text-base text-slate-700 space-y-1">
              <li>Cloud hosting and infrastructure (e.g., Google Cloud)</li>
              <li>Messaging and telecommunication services (e.g., Telnyx)</li>
              <li>Payment processing (e.g., Stripe)</li>
              <li>Analytics and performance monitoring</li>
              <li>Customer support tools</li>
            </ul>

            <h3 className="text-sm md:text-base font-semibold text-slate-900">
              5.2 Legal Compliance and Protection
            </h3>
            <p className="text-sm md:text-base text-slate-700">
              We may disclose your information where required to do so by law or
              in response to valid legal requests, such as subpoenas, court
              orders, or government regulations, or to protect the rights,
              property, and safety of CleanBid.ai, our users, or others.
            </p>

            <h3 className="text-sm md:text-base font-semibold text-slate-900">
              5.3 Business Transfers
            </h3>
            <p className="text-sm md:text-base text-slate-700">
              In the event of a merger, acquisition, reorganization, or sale of
              assets, your information may be transferred as part of that
              transaction. We will take reasonable steps to ensure your privacy is
              protected.
            </p>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              6. Cookies and Tracking Technologies
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              We use cookies and similar technologies to:
            </p>
            <ul className="list-disc pl-5 text-sm md:text-base text-slate-700 space-y-1">
              <li>Enable core functionality</li>
              <li>Remember your preferences</li>
              <li>Analyze usage and performance</li>
              <li>Improve the platform experience</li>
            </ul>
            <p className="text-sm md:text-base text-slate-700">
              You can choose to disable cookies in your browser settings, but this
              may affect certain features of the platform.
            </p>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              7. Data Retention
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              We retain your information for as long as:
            </p>
            <ul className="list-disc pl-5 text-sm md:text-base text-slate-700 space-y-1">
              <li>Your account is active</li>
              <li>It is necessary to provide our services</li>
              <li>We have a legitimate business need to do so</li>
              <li>Required by law or legal obligations</li>
            </ul>
            <p className="text-sm md:text-base text-slate-700">
              You may request deletion of your data, subject to certain legal or
              contractual restrictions.
            </p>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              8. Security
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              We implement administrative, technical, and physical safeguards
              designed to protect your information. However, no method of
              transmission or storage is completely secure, and we cannot
              guarantee absolute security.
            </p>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              9. Your Rights
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              Depending on your location, you may have rights to:
            </p>
            <ul className="list-disc pl-5 text-sm md:text-base text-slate-700 space-y-1">
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to or restrict certain processing</li>
              <li>Request data portability</li>
              <li>Withdraw consent where processing is based on consent</li>
            </ul>
            <p className="text-sm md:text-base text-slate-700">
              To exercise these rights, contact us at{' '}
              <span className="font-mono">support@cleanbid.ai</span>.
            </p>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              10. Children&apos;s Privacy
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              CleanBid.ai is not intended for individuals under 18 years of age.
              We do not knowingly collect personal information from children. If
              we become aware that we have collected such information, we will
              take steps to delete it.
            </p>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              11. SMS &amp; TCPA Compliance
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              By providing your phone number and using CleanBid&apos;s SMS
              features, you consent to receiving:
            </p>
            <ul className="list-disc pl-5 text-sm md:text-base text-slate-700 space-y-1">
              <li>Transactional messages related to your account</li>
              <li>Service notifications and alerts</li>
              <li>Other messages you explicitly opt in to receive</li>
            </ul>
            <p className="text-sm md:text-base text-slate-700">
              You can opt out of SMS at any time by replying &quot;STOP&quot;.
              We do not share SMS opt-in information with third parties except
              carriers and providers necessary to deliver messages.
            </p>
            <p className="text-sm md:text-base text-slate-700">
              Reply <strong>HELP</strong> for help. Message frequency varies.
              Message and data rates may apply. For assistance, contact{' '}
              <a href="mailto:info@cleanbid.ai" className="font-medium underline">
                info@cleanbid.ai
              </a>.
            </p>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              12. Changes to This Privacy Policy
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              We may update this Privacy Policy from time to time. We will post
              the updated version at{' '}
              <span className="font-mono">https://cleanbid.ai/privacy</span> with
              a revised &quot;Last updated&quot; date.
            </p>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              13. Contact Us
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              If you have questions about this Privacy Policy, please contact us
              at:
            </p>
            <p className="text-sm md:text-base text-slate-700">
              CleanBid.ai <br />
              Email:{' '}
              <a
                href="mailto:support@cleanbid.ai"
                className="underline text-slate-900"
              >
                support@cleanbid.ai
              </a>
              <br />
              Website:{' '}
              <a
                href="https://cleanbid.ai"
                target="_blank"
                rel="noreferrer"
                className="underline text-slate-900"
              >
                cleanbid.ai
              </a>
            </p>
          </section>
        </div>
      </main>

      <FooterSection />
    </>
  );
};

export default Privacy;
